import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { CustomSwitch } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import CommonDatePicker, { DatePickerValidationReason } from '../../components/date-picker/CommonDatePicker';
import { CustomDrawer } from '../../components/drawer/CustomDrawer';
import { CommonModal } from '../../components/modal/modal';
import { CustomSelect } from '../../components/select/CustomSelect';
import { TextFieldComponent } from '../../components/text-field/text-field-component';
import { colors } from '../../components/theme/theme';
import {
    bankNameValidation,
    sanitizeBankNameInput,
    BANK_NAME_MAX_LENGTH,
    getAmountMaxValue,
    isAmountOverMax,
    getAmountMaxLength,
    AMOUNT_MAX_VALUE,
    AMOUNT_PRINCIPAL_THRESHOLD,
} from '../../components/validations/validations';
import {
    BankAccountApiResponse,
    createBankAccount,
    CreateBankAccountPayload,
    getBankAccountsList,
} from '../../services/bank-account-services';
import { CreatePaymentPayload, makePayment } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { invalidDatePickerUserMessage } from '../../utils/date-picker-invalid-date';
import {
    trackBankAccountAction,
    trackBankAccountActionFailed,
    trackPaymentCompleted,
    trackPaymentFailed,
} from '../../utils/google-analytics';
import { formatCurrency, formatInET, regex } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import { BankAccountCardWrapper } from '../bank-accounts/BankAccountStyles';

import { MakePaymentWrapper } from './PaymentsStyles';

type WhenToPayOption = 'today' | 'custom';
type PaymentMethodOption = 'card' | 'bank';

// Shared by all CustomSelect fields on this page (select contract, country, account type): keeps
// their border/background/focus styling in sync without repeating the same sx object per field.
const customSelectFieldSx = {
    borderRadius: 2,
    bgcolor: colors.fullWhite,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.greyPreset[20],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.greyPreset[20],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primaryLight[100],
    },
};

interface SavedBankAccount {
    id: string;
    accountType: string;
    bankName: string;
    swiftBicCode: string;
    accountNumber: string;
    accountId: string;
    isActive: boolean;
    aspireIsActive?: boolean;
}

export const CreatePayment: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const contractsFromStore = useSelector((state: RootState) => state.contracts.contracts);
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const impersonation = useSelector((state: RootState) => state.impersonation);

    // Get contract data from navigation state
    const contractFromState = location.state?.contract;
    const [selectedContract, setSelectedContract] = useState<string>('');
    const [paymentAmount, setPaymentAmount] = useState('0.00');
    const [whenToPay, setWhenToPay] = useState<WhenToPayOption>();
    // const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption>('card');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption>(); // Changed default to 'bank' since card is hidden
    const [customDate, setCustomDate] = useState<Dayjs | null>(null);
    const [customDatePickerError, setCustomDatePickerError] = useState('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    // const [cardNumber, setCardNumber] = useState('');
    // const [expiryMonth, setExpiryMonth] = useState('');
    // const [expiryYear, setExpiryYear] = useState('');
    // const [cvv, setCvv] = useState('');
    // const [nameOnCard, setNameOnCard] = useState('');
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    const selectedAccountIdRef = useRef<string | null>(null);
    selectedAccountIdRef.current = selectedAccountId;
    const [showAddAccountForm, setShowAddAccountForm] = useState(false);
    const [country, setCountry] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [reEnterAccountNumber, setReEnterAccountNumber] = useState('');
    const [nickName, setNickName] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountType, setAccountType] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [reEnterRoutingNumber, setReEnterRoutingNumber] = useState('');
    const [savedBankAccounts, setSavedBankAccounts] = useState<SavedBankAccount[]>([]);
    const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
    const [addAccountFormErrors, setAddAccountFormErrors] = useState<Record<string, string>>({});
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [visibleAccounts, setVisibleAccounts] = useState<Record<string, boolean>>({});
    // State for showing/hiding sensitive fields in add account form
    const [showFormAccountNumber, setShowFormAccountNumber] = useState(false);
    const [showFormReEnterAccountNumber, setShowFormReEnterAccountNumber] = useState(false);
    const [showFormRoutingNumber, setShowFormRoutingNumber] = useState(false);
    const [showFormReEnterRoutingNumber, setShowFormReEnterRoutingNumber] = useState(false);
    // Transform contracts from Redux store to dropdown format
    const contracts = contractsFromStore.map(contract => ({
        id: contract.Id.toString(),
        label: contract.AspireId || `Contract ${contract.Id}`,
        aspireId: contract.AspireId,
    }));

    const toggleAccountVisibility = (accountId: string) => {
        setVisibleAccounts(prev => ({
            ...prev,
            [accountId]: !prev[accountId],
        }));
    };

    // Fetch bank accounts
    const fetchBankAccounts = useCallback(
        async (selectNewAccountId?: string, showLoaderParam: boolean = true) => {
            try {
                setBankAccountsLoading(true);
                // Show loader explicitly before API call to ensure it displays during the ~10s fetch
                if (showLoaderParam) {
                    dispatch(showLoader());
                }
                const response = await getBankAccountsList(false); // false: don't show loader in API layer (we manage it here)
                if (response?.statusCode === 200 && Array.isArray(response?.data)) {
                    const mappedAccounts: SavedBankAccount[] = response?.data?.map(
                        (bankAccount: BankAccountApiResponse) => ({
                            id: bankAccount?.Id.toString(),
                            accountType: bankAccount?.AccountType || bankAccount?.accountType || 'Savings',
                            bankName: bankAccount?.BankName || 'Bank',
                            swiftBicCode: bankAccount?.IdentificationNumber || 'N/A',
                            accountNumber: bankAccount?.AccountNumber || '',
                            accountId: bankAccount?.AspireEntityOId || bankAccount?.AspireRecordId || '',
                            isActive: bankAccount?.IsActive,
                            aspireIsActive: bankAccount?.AspireIsActive,
                        }),
                    );
                    setSavedBankAccounts(mappedAccounts);
                    if (selectNewAccountId) {
                        const foundAccount = mappedAccounts.find(account => account.id === selectNewAccountId);
                        if (foundAccount) {
                            setSelectedAccountId(foundAccount.id);
                        } else if (mappedAccounts.length > 0) {
                            setSelectedAccountId(mappedAccounts[0].id);
                        }
                    } else if (mappedAccounts.length > 0 && !selectedAccountIdRef.current) {
                        setSelectedAccountId(mappedAccounts[0].id);
                    }
                }
            } catch (error) {
                console.error('Error fetching bank accounts:', error);
                const err = error as { response?: { data?: { message?: string } }; message?: string };
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: err?.response?.data?.message || err?.message || 'Failed to Load Bank Accounts',
                        severity: 'error',
                    }),
                );
            } finally {
                setBankAccountsLoading(false);
                // Hide loader only after response is fully processed (not before API completes)
                dispatch(hideLoader());
            }
        },
        [dispatch],
    );

    // Utility to map country name to code
    const getCountryCode = (country: string) => {
        const trimmed = country.trim();
        if (trimmed === 'United States') return 'USA';
        if (trimmed === 'Canada') return 'CAN';
        return trimmed;
    };

    // Handle add account form submission
    const handleAddAccount = async () => {
        const errors: Record<string, string> = {};

        // Validate all required fields
        if (!country || country.trim() === '') {
            errors.country = 'Country is required';
        }
        const bankNameError = bankNameValidation(bankName);
        if (bankNameError) {
            errors.bankName = bankNameError;
        }
        if (!accountType) {
            errors.accountType = 'Account Type is required';
        }

        // Validate account number: min 6, max 16 (matches backend CreateBankAccountDto). Cannot be all same digit.
        let isAccountNumberValid = true;
        if (!accountNumber || accountNumber.trim() === '') {
            errors.accountNumber = 'Account Number is required';
            isAccountNumberValid = false;
        } else if (accountNumber.trim().length < 6) {
            errors.accountNumber = 'Account number must be at least 6 characters';
            isAccountNumberValid = false;
        } else if (accountNumber.trim().length > 16) {
            errors.accountNumber = 'Account number must be at most 16 characters';
            isAccountNumberValid = false;
        } else if (/^(\d)\1+$/.test(accountNumber.trim())) {
            errors.accountNumber = 'Account number cannot contain only the same digit (e.g., 000000000, 111111111)';
            isAccountNumberValid = false;
        }

        // Validate re-enter account number - apply same validation rules as account number
        if (!reEnterAccountNumber || reEnterAccountNumber.trim() === '') {
            errors.reEnterAccountNumber = 'Please re-enter account number';
        } else if (reEnterAccountNumber.trim().length < 6) {
            errors.reEnterAccountNumber = 'Re-enter Account Number must be at least 6 characters';
        } else if (reEnterAccountNumber.trim().length > 16) {
            errors.reEnterAccountNumber = 'Re-enter Account Number must be at most 16 characters';
        } else if (isAccountNumberValid && accountNumber.trim() !== reEnterAccountNumber.trim()) {
            errors.reEnterAccountNumber = 'Account numbers do not match';
        } else if (!isAccountNumberValid) {
            errors.reEnterAccountNumber = 'Please fix the Account Number field first';
        }

        // Validate routing number: exactly 9 digits, numeric only, cannot start with 0, cannot be all same digit (matches backend)
        if (!routingNumber || routingNumber.trim() === '') {
            errors.routingNumber = 'Routing Number is required';
        } else if (!/^\d+$/.test(routingNumber.trim())) {
            errors.routingNumber = 'Routing Number must contain only numbers';
        } else if (routingNumber.trim().length !== 9) {
            errors.routingNumber = 'Routing Number must be exactly 9 digits';
        } else if (routingNumber.trim().startsWith('0')) {
            errors.routingNumber = 'Routing number cannot start with 0';
        } else if (/^(\d)\1+$/.test(routingNumber.trim())) {
            errors.routingNumber = 'Routing Number cannot contain only the same digit (e.g., 000000000, 111111111)';
        }

        // Validate re-enter routing number
        if (!reEnterRoutingNumber || reEnterRoutingNumber.trim() === '') {
            errors.reEnterRoutingNumber = 'Re-enter Routing Number is required';
        } else if (routingNumber.trim() !== reEnterRoutingNumber.trim()) {
            errors.reEnterRoutingNumber = 'Routing Numbers do not match';
        } else if (reEnterRoutingNumber.trim().length !== 9) {
            errors.reEnterRoutingNumber = 'Routing Number must be exactly 9 digits';
        }

        if (Object.keys(errors).length > 0) {
            setAddAccountFormErrors(errors);
            return;
        }

        try {
            // Show loader manually - it will stay visible until fetchBankAccounts completes
            dispatch(showLoader());

            // Map account type to API format
            // The API accepts "Checking" or "Savings" as separate values
            let mappedAccountType = accountType;
            const savingsAccountLabel = t('payments.createPayment.accountTypes.savingsAccount');
            const checkingAccountLabel = t('payments.createPayment.accountTypes.checkingAccount');
            const currentAccountLabel = t('payments.createPayment.accountTypes.currentAccount');

            if (accountType === savingsAccountLabel) {
                mappedAccountType = 'Savings';
            } else if (accountType === checkingAccountLabel || accountType === currentAccountLabel) {
                mappedAccountType = 'Checking';
            }

            const payload: CreateBankAccountPayload = {
                bankName: bankName.trim(),
                accountNumber: accountNumber.trim(),
                country: getCountryCode(country),
                accountType: mappedAccountType,
                routingNumber: routingNumber.trim(),
                nickName: nickName.trim() || null,
            };

            const response = await createBankAccount(payload, true);

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Track successful bank account creation
                trackBankAccountAction('added', 'payment_flow', bankName.trim());

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Bank Account Added Successfully',
                        severity: 'success',
                    }),
                );

                // Reset form and close drawer
                handleCloseAddAccountDrawer();

                // Fetch updated list and select the newly created account
                // The API response should contain the new account ID, but if not, we'll select the first one
                // fetchBankAccounts will complete and hide the loader in its finally block
                const newAccountId = response.data?.Id?.toString();
                await fetchBankAccounts(newAccountId);
            } else {
                throw new Error(response.message || 'Failed to Create Bank Account');
            }
        } catch (error) {
            console.error('Error creating bank account:', error);
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to Create Bank Account';
            trackBankAccountActionFailed('add', errorMessage, 'payment_flow', bankName.trim());
            // Hide loader on error
            dispatch(hideLoader());
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errorMessage,
                    severity: 'error',
                }),
            );
        } finally {
            setBankAccountsLoading(false);
            dispatch(hideLoader());
        }
    };

    // Handle close add account drawer
    const handleCloseAddAccountDrawer = () => {
        setShowAddAccountForm(false);
        setCountry('');
        setAccountNumber('');
        setReEnterAccountNumber('');
        setNickName('');
        setBankName('');
        setAccountType('');
        setRoutingNumber('');
        setReEnterRoutingNumber('');
        setAddAccountFormErrors({});
        // Reset visibility states
        setShowFormAccountNumber(false);
        setShowFormReEnterAccountNumber(false);
        setShowFormRoutingNumber(false);
        setShowFormReEnterRoutingNumber(false);
    };

    // Helper function to get the selected contract object
    const getSelectedContractObject = () => {
        if (!selectedContract) return null;
        return contractsFromStore.find(c => c.Id.toString() === selectedContract);
    };

    // Determine if amount field should be editable based on contract type
    const isAmountEditable = () => {
        const contract = getSelectedContractObject();
        if (!contract) return true; // Default to editable if no contract selected

        // Determine if it's Consumer or Commercial
        const isConsumer = userDetails?.AccountType === 'I';

        // Consumer + Loan: Amount field is editable
        // Consumer + Lease: Amount field is non-editable
        // Commercial + Loan: Amount field is non-editable
        // Commercial + Lease: Amount field is non-editable
        if (isConsumer && contract.IsLoan) {
            return true; // Consumer + Loan: editable
        }
        return false; // All other cases: non-editable
    };

    // Determine if card/bank payment methods should be enabled based on contract type
    const isPaymentMethodsEnabled = () => {
        const contract = getSelectedContractObject();
        if (!contract) return false;

        const isConsumer = userDetails?.AccountType === 'I';

        // Only allow card/bank payments for Consumer Loan (I + IsLoan)
        // Disable for all other cases: Consumer Lease, Commercial Loan, Commercial Lease
        return isConsumer && contract.IsLoan;
    };

    // Set selected contract from navigation state
    useEffect(() => {
        if (contractFromState?.id) {
            // Find the contract by ID
            const contractId = contractFromState.id.toString();
            setSelectedContract(contractId);
        }
    }, [contractFromState]);

    const contractEmiAmount = contractFromState?.emiAmount;

    // Prepopulate amount when contract is selected and field is not editable
    useEffect(() => {
        const contract = contractsFromStore.find(c => c.Id.toString() === selectedContract);
        if (!contract || !contract.EMIAmount) return;

        const isConsumer = userDetails?.AccountType === 'I';
        const shouldPrepopulate = !(isConsumer && contract.IsLoan);
        if (shouldPrepopulate) {
            const formattedAmount = parseFloat(contractEmiAmount).toFixed(2);
            setPaymentAmount(formattedAmount);
        } else if (isConsumer && contract.IsLoan) {
            setPaymentAmount('0.00');
        }
    }, [selectedContract, contractsFromStore, userDetails, contractEmiAmount]);

    // Fetch bank accounts on component mount
    useEffect(() => {
        fetchBankAccounts();
    }, [fetchBankAccounts]);

    const formattedToday = formatInET(new Date(), { month: 'short', day: 'numeric', year: 'numeric' });
    const mapPaymentDatePickerReasonToMessage = (
        reason: DatePickerValidationReason,
        meta?: { rawInput: string },
    ): string => {
        if (reason === 'invalidDate') {
            return invalidDatePickerUserMessage(meta?.rawInput ?? '', 'generic', t);
        }
        if (reason === 'disableFuture') return t('datePicker.generic.disableFuture');
        if (reason === 'disablePast') return t('datePicker.generic.disablePast');
        if (reason === 'minDate') return t('datePicker.generic.minDate');
        if (reason === 'maxDate') return t('datePicker.payment.maxDateMaturityPlus90Days');
        return t('datePicker.generic.invalid');
    };

    // Validate if all required fields are filled
    const isPaymentFormValid = () => {
        const hasContract = !!selectedContract;
        const hasAmount = !!paymentAmount && parseFloat(paymentAmount) > 0;
        const hasPaymentDate =
            whenToPay === 'today' || (whenToPay === 'custom' && customDate !== null && !customDatePickerError);

        // Only require payment method for consumer loans
        const hasPaymentMethod = !isPaymentMethodsEnabled() || paymentMethod === 'bank';
        const hasBankAccount = !isPaymentMethodsEnabled() || (!!selectedAccountId && savedBankAccounts.length > 0);

        return hasContract && hasAmount && hasPaymentDate && hasPaymentMethod && hasBankAccount;
    };

    // Shared by handleMakePayment and handleConfirmPayment: checks the current paymentAmount against
    // the selected contract's allowed max (outstanding balance; cap at 99,999,999.99 if balance >= 100M).
    // Dispatches an error toast and returns false when the amount is not allowed.
    const validatePaymentAmountWithinMax = (): boolean => {
        const contract = getSelectedContractObject();
        if (contract) {
            const outstandingBalance = Number(contract.OutstandingBalance) || 0;
            const effectiveMax = getAmountMaxValue(outstandingBalance);
            const amount = parseFloat(paymentAmount) || 0;
            if (isAmountOverMax(amount, effectiveMax)) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message:
                            outstandingBalance >= AMOUNT_PRINCIPAL_THRESHOLD
                                ? 'Amount Cannot Exceed 99,999,999.99'
                                : 'The Amount Cannot Exceed The Outstanding Balance.',
                        severity: 'error',
                    }),
                );
                return false;
            }
        }
        return true;
    };

    // Shared by handleConfirmPayment and handleBack: navigates to ContractDetails using the full
    // contract from the store when available, otherwise falls back to the minimal state we have.
    const navigateToContractDetailsOrFallback = (contractRef: NonNullable<typeof contractFromState>) => {
        const fullContract = contractsFromStore.find(
            c => c.Id.toString() === contractRef.id.toString() || c.AspireId === contractRef.aspireId,
        );

        if (fullContract) {
            navigate(NavigationConfig.ContractDetails, {
                state: {
                    contract: fullContract,
                },
            });
        } else {
            // If contract not found in store, pass what we have
            navigate(NavigationConfig.ContractDetails, {
                state: {
                    contract: {
                        Id: contractRef.id,
                        AspireId: contractRef.aspireId,
                    },
                },
            });
        }
    };

    // Handle make payment - opens confirmation modal
    const handleMakePayment = () => {
        if (!isPaymentFormValid()) {
            const formMessage =
                whenToPay === 'custom' && customDatePickerError
                    ? customDatePickerError
                    : 'Please Fill All Required Fields';
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: formMessage,
                    severity: 'error',
                }),
            );
            return;
        }

        // Card payment restrictions
        const contract = getSelectedContractObject();
        const isConsumerLoan = contract?.IsLoan && userDetails?.AccountType === 'I';

        if (paymentMethod === 'card') {
            // Block card payments for non-consumer-loans
            if (!isConsumerLoan) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Card payments are only available for consumer loans.',
                        severity: 'error',
                    }),
                );
                return;
            }

            // Block card payments if AutoPay is enabled
            if (contract?.AutoPay) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Card payments are not available when AutoPay is enabled.',
                        severity: 'error',
                    }),
                );
                return;
            }
        }

        // Validate amount does not exceed allowed max (outstanding balance; cap at 99,999,999.99 if balance >= 100M)
        if (!validatePaymentAmountWithinMax()) {
            return;
        }
        setShowConfirmationModal(true);
    };

    // Handle payment confirmation - actually submits the payment
    const handleConfirmPayment = async () => {
        setShowConfirmationModal(false);

        if (impersonation?.isImpersonating) {
            return;
        }

        try {
            // Revalidate amount does not exceed allowed max before API call
            if (!validatePaymentAmountWithinMax()) {
                return;
            }

            // Format payment date
            // If "pay today" is selected, use current date
            // If "custom date" is selected, use the selected custom date
            // Use local date to avoid timezone conversion issues
            let paymentDateISO: string;
            if (whenToPay === 'today') {
                // Use current date in local timezone, format as YYYY-MM-DDTHH:mm:ss.SSSZ
                const todayLocal = dayjs();
                paymentDateISO = `${todayLocal.format('YYYY-MM-DD')}T00:00:00.000Z`;
            } else if (whenToPay === 'custom' && customDate) {
                // Use custom date in local timezone
                paymentDateISO = `${customDate.format('YYYY-MM-DD')}T00:00:00.000Z`;
            } else {
                throw new Error('Payment date is required');
            }

            const payload: CreatePaymentPayload = {
                contractId: selectedContract,
                amount: parseFloat(paymentAmount),
                paymentDate: paymentDateISO,
                bankAccountId: selectedAccountId || '',
            };

            // Show loader explicitly before payment API call to ensure it displays during processing
            dispatch(showLoader());
            const response = await makePayment(payload, false); // false: we manage loader here

            const paymentType = whenToPay === 'today' ? 'one_time' : 'scheduled';

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Track successful payment
                trackPaymentCompleted(parseFloat(paymentAmount), selectedContract, 'Bank Account', paymentType);

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Payment Created Successfully',
                        severity: 'success',
                    }),
                );

                // Navigate back to contract details with the contract data
                if (contractFromState) {
                    navigateToContractDetailsOrFallback(contractFromState);
                } else {
                    // If no contract data in state, navigate back to contracts list
                    navigate(NavigationConfig.ContractOverview);
                }
            } else {
                throw new Error(response.message || 'Failed to create payment');
            }
        } catch (error) {
            console.error('Error creating payment:', error);
            const err = error as { response?: { data?: { message?: string } }; message?: string };

            // Track failed payment
            trackPaymentFailed(
                selectedContract,
                err?.response?.data?.message || err?.message,
                whenToPay === 'today' ? 'one_time' : 'scheduled',
            );

            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: err?.response?.data?.message || err?.message || 'Failed to Create Payment',
                    severity: 'error',
                }),
            );
        } finally {
            // Hide loader after the payment attempt completes (success or error)
            dispatch(hideLoader());
        }
    };

    // Handle close confirmation modal
    const handleCloseConfirmationModal = () => {
        setShowConfirmationModal(false);
    };

    // Get navigation source flags
    const fromContracts = location.state?.fromContracts;
    const fromPayments = location.state?.fromPayments;
    const fromDashboard = location.state?.fromDashboard;

    // Handle back button navigation
    const handleBack = () => {
        // If coming from UpcomingPayments page
        if (fromPayments) {
            navigate(NavigationConfig.Payments);
        }
        // If coming from Dashboard page
        else if (fromDashboard) {
            navigate(NavigationConfig.Home);
        }
        // If coming from ContractDetails page
        else if (fromContracts && contractFromState) {
            navigateToContractDetailsOrFallback(contractFromState);
        }
        // Default fallback
        else {
            navigate(NavigationConfig.ContractOverview);
        }
    };

    // Shared by the "Pay Today" and "Choose Custom Date" option cards: renders the calendar icon,
    // filled/highlighted when its option is selected.
    const renderWhenToPayCalendarIcon = (selected: boolean) => (
        <Box>
            <span
                className={selected ? 'icon-calendar-fill' : 'icon-calendar'}
                style={{
                    fontSize: '20px',
                    color: selected ? colors.bluePreset[90] : colors.greyPreset[40],
                }}
            />
        </Box>
    );

    const formatAccountNumber = (accountNumber: string, isVisible: boolean) => {
        if (isVisible) {
            return accountNumber;
        }
        const lastFour = accountNumber.slice(-4);
        const starsCount = accountNumber.length - 4;
        const stars = '*'.repeat(starsCount);
        return `${stars} ${lastFour}`;
    };

    return (
        <MakePaymentWrapper>
            <ButtonComponent
                variant="outlined"
                SMButton={true}
                startIcon={<span className="icon-arrow-left" style={{ fontSize: 16 }} />}
                onClick={handleBack}
                sx={{ width: 'fit-content', mb: 3 }}
            >
                {t('common.Back')}
            </ButtonComponent>
            <Grid container spacing={3}>
                {/* Left Column - Make Payment Form */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Stack className="makePaymentFormWrapper">
                        <Typography
                            variant="h5"
                            sx={{ p: 2, color: colors.fullBlack, borderBottom: `1px solid ${colors.greyPreset[30]}` }}
                        >
                            {t('payments.createPayment.title')}
                        </Typography>
                        {impersonation?.isImpersonating && (
                            <Stack
                                sx={{
                                    p: 1.5,
                                    mx: 2,
                                    mt: 2,
                                    backgroundColor: colors.warningPreset[40],
                                    borderRadius: 1,
                                    border: `1px solid ${colors.warningPreset[30]}`,
                                }}
                            >
                                <Typography variant="body2" color={colors.primaryLight[100]}>
                                    {t('payments.impersonatingNote') ||
                                        'The payment button is disabled while impersonating. You can review the payment details but cannot submit the payment.'}
                                </Typography>
                            </Stack>
                        )}
                        <Stack sx={{ gap: 2, p: 2 }}>
                            {/* Select Contract */}
                            <CustomSelect
                                title={t('payments.createPayment.selectContract')}
                                required
                                disabled
                                value={
                                    selectedContract ? contracts.find(c => c.id === selectedContract)?.label || '' : ''
                                }
                                onChange={value => {
                                    const contract = contracts.find(c => c.label === value);
                                    setSelectedContract(contract?.id || '');
                                }}
                                options={contracts.map(contract => contract.label)}
                                placeholder={t('payments.createPayment.selectContractId')}
                                sx={customSelectFieldSx}
                            />
                            {/* Payment Amount */}
                            <Box>
                                {(() => {
                                    // Max amount: 99,999,999.99 when principal/balance >= 100M; otherwise capped by outstanding balance
                                    const contract = getSelectedContractObject();
                                    const outstandingBalance =
                                        contract?.OutstandingBalance != null
                                            ? Number(contract.OutstandingBalance)
                                            : null;
                                    const effectiveMax =
                                        outstandingBalance != null
                                            ? getAmountMaxValue(outstandingBalance)
                                            : AMOUNT_MAX_VALUE;
                                    const maxLength =
                                        outstandingBalance != null ? getAmountMaxLength(outstandingBalance) : 11;
                                    // Shared by onPaste/onChange below: warns (via toast) when the typed/pasted
                                    // amount exceeds the allowed max, without altering the numeric comparison itself.
                                    const showAmountExceedsMaxToast = () => {
                                        dispatch(
                                            openToast({
                                                show: true,
                                                isAlertOpen: true,
                                                message:
                                                    outstandingBalance != null &&
                                                    outstandingBalance >= AMOUNT_PRINCIPAL_THRESHOLD
                                                        ? 'Amount Cannot Exceed 99,999,999.99'
                                                        : 'The Amount Cannot Exceed The Outstanding Balance.',
                                                severity: 'error',
                                            }),
                                        );
                                    };
                                    return (
                                        <TextFieldComponent
                                            label={t('payments.createPayment.paymentAmount')}
                                            required
                                            value={paymentAmount}
                                            inputMode="decimal"
                                            onKeyDown={e => {
                                                // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
                                                const allowedKeys = [
                                                    'Backspace',
                                                    'Delete',
                                                    'Tab',
                                                    'Escape',
                                                    'Enter',
                                                    '.',
                                                    'ArrowLeft',
                                                    'ArrowRight',
                                                    'ArrowUp',
                                                    'ArrowDown',
                                                    'Home',
                                                    'End',
                                                ];
                                                // Allow Ctrl/Cmd + A, C, V, X for copy/paste/cut/select all
                                                if (
                                                    (e.ctrlKey || e.metaKey) &&
                                                    ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())
                                                ) {
                                                    return;
                                                }
                                                // Allow numbers (0-9) and decimal point
                                                if (allowedKeys.includes(e.key) || /^[0-9]$/.test(e.key)) {
                                                    return;
                                                }
                                                // Block all other keys (alphanumeric characters, symbols, etc.)
                                                e.preventDefault();
                                            }}
                                            onPaste={e => {
                                                e.preventDefault();
                                                const pastedText = e.clipboardData.getData('text/plain');
                                                // Only allow pasting if it matches the payment amount regex
                                                if (regex.paymentAmountRegExp.test(pastedText) || pastedText === '') {
                                                    const value = pastedText;
                                                    if (value) {
                                                        const numericValue = parseFloat(value);
                                                        if (!isNaN(numericValue) && numericValue > effectiveMax) {
                                                            showAmountExceedsMaxToast();
                                                            return;
                                                        }
                                                    }
                                                    setPaymentAmount(value);
                                                } else {
                                                    dispatch(
                                                        openToast({
                                                            show: true,
                                                            isAlertOpen: true,
                                                            message:
                                                                'Invalid Characters. Only Numbers And Decimal Point Are Allowed.',
                                                            severity: 'error',
                                                        }),
                                                    );
                                                }
                                            }}
                                            onChange={e => {
                                                const value = e.target.value;
                                                // Validate format first - only allow numbers and decimal point
                                                if (regex.paymentAmountRegExp.test(value) || value === '') {
                                                    if (value) {
                                                        const numericValue = parseFloat(value);
                                                        if (!isNaN(numericValue) && numericValue > effectiveMax) {
                                                            showAmountExceedsMaxToast();
                                                            return;
                                                        }
                                                    }
                                                    setPaymentAmount(value);
                                                }
                                            }}
                                            disabled={!isAmountEditable()}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            }}
                                            startIcon={<Typography color="textSecondary">$</Typography>}
                                            maxLength={maxLength}
                                        />
                                    );
                                })()}
                            </Box>

                            {/* When to Pay */}
                            <Stack>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: colors.primaryLight[100],
                                        mb: 0.5,
                                    }}
                                >
                                    {t('payments.createPayment.whenToPay')}{' '}
                                    <Typography component="span" sx={{ color: colors.errorPreset[10] }}>
                                        *
                                    </Typography>
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box sx={{ position: 'relative', height: '100%' }}>
                                            <Stack
                                                onClick={() => {
                                                    setWhenToPay('today');
                                                    setCustomDatePickerError('');
                                                    // setDatePickerOpen(true);
                                                }}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    border: `1px solid ${
                                                        whenToPay === 'today'
                                                            ? colors.bluePreset[90]
                                                            : colors.greyPreset[30]
                                                    }`,
                                                    bgcolor:
                                                        whenToPay === 'today'
                                                            ? colors.bluePreset[110]
                                                            : colors.fullWhite,
                                                    cursor: 'pointer',
                                                    height: '100%',
                                                    '&:hover': {
                                                        borderColor: colors.bluePreset[90],
                                                        bgcolor: colors.bluePreset[110],
                                                    },
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: colors.primaryLight[100],
                                                                lineHeight: '21px',
                                                            }}
                                                        >
                                                            {t('payments.createPayment.payToday')}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                lineHeight: '15px',
                                                            }}
                                                        >
                                                            {formattedToday}
                                                        </Typography>
                                                    </Box>
                                                    {renderWhenToPayCalendarIcon(whenToPay === 'today')}
                                                </Stack>
                                            </Stack>
                                            {/* Hidden date picker input that serves as anchor */}
                                            {/* {whenToPay === 'today' && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        width: '1px',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <CommonDatePicker
                                                        value={customDate}
                                                        onChange={newValue => {
                                                            setCustomDate(newValue);
                                                            if (newValue) {
                                                                setDatePickerOpen(false);
                                                            }
                                                        }}
                                                        disabled={true}
                                                        minDate={dayjs().endOf('day')}
                                                        maxDate={dayjs().startOf('day')}
                                                        open={datePickerOpen}
                                                        // onOpen={() => setDatePickerOpen(true)}
                                                        // onClose={() => setDatePickerOpen(false)}
                                                        hideInput={true}
                                                    />
                                                </Box>
                                            )} */}
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box sx={{ position: 'relative', height: '100%' }}>
                                            <Stack
                                                onClick={() => {
                                                    setWhenToPay('custom');
                                                    setDatePickerOpen(true);
                                                }}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    border: `1px solid ${
                                                        whenToPay === 'custom'
                                                            ? colors.bluePreset[90]
                                                            : colors.greyPreset[30]
                                                    }`,
                                                    bgcolor:
                                                        whenToPay === 'custom'
                                                            ? colors.bluePreset[110]
                                                            : colors.fullWhite,
                                                    cursor: 'pointer',
                                                    height: '100%',
                                                    '&:hover': {
                                                        borderColor: colors.bluePreset[90],
                                                        bgcolor: colors.bluePreset[110],
                                                    },
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: colors.primaryLight[100],
                                                                lineHeight: '21px',
                                                            }}
                                                        >
                                                            {t('payments.createPayment.chooseCustomDate')}
                                                        </Typography>
                                                        {customDate ? (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: colors.primaryLight[100],
                                                                    lineHeight: '21px',
                                                                }}
                                                            >
                                                                {customDate.format('MM/DD/YYYY')}
                                                            </Typography>
                                                        ) : (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    lineHeight: '15px',
                                                                }}
                                                            >
                                                                {t('payments.createPayment.selectDateWhenToPay')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {renderWhenToPayCalendarIcon(whenToPay === 'custom')}
                                                </Stack>
                                            </Stack>
                                            {/* Hidden date picker input that serves as anchor */}
                                            {whenToPay === 'custom' && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        width: '1px',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <CommonDatePicker
                                                        value={customDate}
                                                        onChange={newValue => {
                                                            setCustomDate(newValue);
                                                            setCustomDatePickerError('');
                                                            if (newValue) {
                                                                setDatePickerOpen(false);
                                                            }
                                                        }}
                                                        onPickerError={(reason, _v, meta) => {
                                                            if (!reason) {
                                                                setCustomDatePickerError('');
                                                                return;
                                                            }
                                                            setCustomDatePickerError(
                                                                mapPaymentDatePickerReasonToMessage(reason, meta),
                                                            );
                                                        }}
                                                        DatePickerErrorValue={customDatePickerError}
                                                        error={Boolean(customDatePickerError)}
                                                        minDate={dayjs().endOf('day')}
                                                        open={datePickerOpen}
                                                        onOpen={() => setDatePickerOpen(true)}
                                                        onClose={() => setDatePickerOpen(false)}
                                                        hideInput={true}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Stack>

                            {/* Choose Payment Method */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: colors.primaryLight[100],
                                        mb: 0.5,
                                    }}
                                >
                                    {t('payments.createPayment.choosePaymentMethod')}{' '}
                                    <Typography component="span" sx={{ color: colors.errorPreset[10] }}>
                                        *
                                    </Typography>
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* Credit & Debit Cards option - commented out */}
                                    {/* <Grid size={{ xs: 12, md: 6 }}>
                                        <Stack
                                            onClick={() => setPaymentMethod('card')}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `1px solid ${paymentMethod === 'card'
                                                    ? colors.warningPreset[30]
                                                    : colors.greyPreset[30]
                                                    }`,
                                                bgcolor:
                                                    paymentMethod === 'card' ? colors.bluePreset[110] : colors.fullWhite,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: colors.warningPreset[20],
                                                    bgcolor: colors.bluePreset[110],
                                                },
                                            }}
                                        >
                                            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                                <Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: colors.primaryLight[100],
                                                            lineHeight: '21px'
                                                        }}
                                                    >
                                                        {t('payments.createPayment.creditDebitCards')}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            lineHeight: '15px',
                                                        }}
                                                    >
                                                        {t('payments.createPayment.payUsingCard')}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <span
                                                        className={paymentMethod === 'card' ? "icon-fill-card" : "icon-outline-card"}
                                                        style={{
                                                            fontSize: '20px',
                                                            color: colors.primaryLight[100],
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Grid> */}
                                    <Grid size={{ xs: 12, md: 12 }}>
                                        <Stack
                                            onClick={() => setPaymentMethod('bank')}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `1px solid ${
                                                    paymentMethod === 'bank'
                                                        ? colors.bluePreset[90]
                                                        : colors.greyPreset[30]
                                                }`,
                                                bgcolor:
                                                    paymentMethod === 'bank'
                                                        ? colors.bluePreset[110]
                                                        : colors.fullWhite,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: colors.bluePreset[90],
                                                    bgcolor: colors.bluePreset[110],
                                                },
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: colors.primaryLight[100],
                                                            lineHeight: '21px',
                                                        }}
                                                    >
                                                        {t('payments.createPayment.bank')}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            lineHeight: '15px',
                                                        }}
                                                    >
                                                        {t('payments.createPayment.paySecurelyThroughBank')}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <span
                                                        className={
                                                            paymentMethod === 'bank' ? 'icon-bank-fill' : 'icon-bank'
                                                        }
                                                        style={{
                                                            fontSize: '20px',
                                                            color:
                                                                paymentMethod === 'bank'
                                                                    ? colors.bluePreset[90]
                                                                    : colors.greyPreset[40],
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Saved Accounts Section */}
                            {isPaymentMethodsEnabled() && paymentMethod === 'bank' && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: colors.primaryLight[100],
                                            }}
                                        >
                                            {t('payments.createPayment.savedAccounts')}
                                        </Typography>
                                        {/* Add Account Button */}
                                        {paymentMethod === 'bank' && (
                                            <ButtonComponent
                                                variant="outlined"
                                                startIcon={<span className="icon-plus" />}
                                                onClick={() => setShowAddAccountForm(true)}
                                                SMButton={true}
                                                sx={{
                                                    alignSelf: 'flex-start',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                {t('payments.createPayment.addAccount')}
                                            </ButtonComponent>
                                        )}
                                    </Stack>
                                    {bankAccountsLoading ? (
                                        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                                            {t('common.loading') || 'Loading bank accounts...'}
                                        </Typography>
                                    ) : savedBankAccounts.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                                            {t('payments.createPayment.noBankAccounts') ||
                                                'No bank accounts found. Please add a bank account.'}
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {savedBankAccounts
                                                ?.filter(
                                                    account => account?.isActive && account?.aspireIsActive !== false,
                                                )
                                                ?.map(account => {
                                                    const isSelected = selectedAccountId === account.id;
                                                    const isVisible = visibleAccounts[account.id] || false;
                                                    return (
                                                        <Grid size={{ xs: 12, lg: 6 }} key={account.id}>
                                                            <BankAccountCardWrapper
                                                                onClick={() => setSelectedAccountId(account.id)}
                                                                sx={{
                                                                    '&::before': {
                                                                        opacity: isSelected ? 0.3 : 0.8,
                                                                    },
                                                                    border: isSelected
                                                                        ? `1px solid ${colors.bluePreset[90]}`
                                                                        : `1px solid ${colors.greyPreset[20]}`,
                                                                    '&:hover': {
                                                                        border: `1px solid ${colors.bluePreset[90]}`,
                                                                    },
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                {/* <Stack direction={'row'}> */}
                                                                <Box className="bank-icon-wrapper">
                                                                    <Box className="bank-icon-name-wrapper">
                                                                        <span
                                                                            className="icon-bank"
                                                                            style={{
                                                                                fontSize: 24,
                                                                                color: colors.primaryLight[100],
                                                                            }}
                                                                        />
                                                                        <Box className="bankContent">
                                                                            <Typography
                                                                                variant="caption"
                                                                                className="info-label"
                                                                            >
                                                                                {account.accountType}
                                                                            </Typography>

                                                                            <Tooltip title={account.bankName}>
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    className="bank-name-text"
                                                                                    noWrap
                                                                                >
                                                                                    {account.bankName}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box>
                                                                        <CustomSwitch
                                                                            checked={true}
                                                                            onChange={() => {}}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                                {/* </Stack> */}

                                                                <Stack className="account-info-container">
                                                                    <Box>
                                                                        <Typography
                                                                            variant="caption"
                                                                            className="info-label"
                                                                        >
                                                                            {t('payments.createPayment.swiftBicCode')}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="subtitle1"
                                                                            className="info-value"
                                                                        >
                                                                            {account.swiftBicCode}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Stack
                                                                        direction="row"
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                    >
                                                                        <Stack>
                                                                            <Typography
                                                                                variant="caption"
                                                                                className="info-label"
                                                                            >
                                                                                {t(
                                                                                    'payments.createPayment.accountNumber',
                                                                                )}
                                                                            </Typography>
                                                                            <Stack className="account-number-container">
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    className="info-value"
                                                                                >
                                                                                    {formatAccountNumber(
                                                                                        account.accountNumber,
                                                                                        isVisible,
                                                                                    )}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    className="visibility-toggle-button"
                                                                                    // onClick={e => {
                                                                                    //     e.stopPropagation();
                                                                                    //     setShowAccountNumber(prev => ({
                                                                                    //         ...prev,
                                                                                    //         [account.id]: !prev[account.id],
                                                                                    //     }));
                                                                                    // }}
                                                                                    onClick={() =>
                                                                                        toggleAccountVisibility(
                                                                                            account.id,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <span
                                                                                        className={
                                                                                            isVisible
                                                                                                ? 'icon-eye'
                                                                                                : 'icon-eye-off'
                                                                                        }
                                                                                        style={{
                                                                                            fontSize: 18,
                                                                                            color: theme.palette.primary
                                                                                                .main,
                                                                                            fontWeight: 600,
                                                                                        }}
                                                                                    />
                                                                                </IconButton>
                                                                            </Stack>
                                                                        </Stack>
                                                                        {/* <Box className="account-id-container">
                                                                            <Typography
                                                                                variant="caption"
                                                                                textAlign="right"
                                                                                className="info-label"
                                                                            >
                                                                                {t('payments.createPayment.accountId')}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="subtitle1"
                                                                                className="info-value"
                                                                            >
                                                                                {account.accountId}
                                                                            </Typography>
                                                                        </Box> */}
                                                                    </Stack>
                                                                </Stack>
                                                            </BankAccountCardWrapper>
                                                        </Grid>
                                                    );
                                                })}
                                        </Grid>
                                    )}
                                </Box>
                            )}

                            {/* Saved Cards Section - commented out (Credit & Debit Cards functionality) */}
                            {/* {paymentMethod === 'card' && (
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: colors.primaryLight[100],
                                            mb: 2,
                                        }}
                                    >
                                        {t('payments.createPayment.savedCards')}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {savedBankAccounts.map(account => {
                                            const isSelected = selectedAccountId === account.id;
                                            const isAccountNumberVisible = showAccountNumber[account.id] || false;
                                            return (
                                                <Grid size={{ xs: 12, lg: 6 }} key={account.id}>
                                                    <BankAccountCardWrapper
                                                        onClick={() => setSelectedAccountId(account.id)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            border: isSelected
                                                                ? `1px solid ${colors.creamPreset[40]}`
                                                                : `1px solid ${colors.greyPreset[20]}`,
                                                            '&:hover': {
                                                                backgroundColor: colors.bluePreset[110],
                                                                border: `1px solid ${colors.creamPreset[40]}`,
                                                            },
                                                            width: '100%',
                                                            backgroundColor: isSelected ? colors.bluePreset[110] : colors.fullWhite,
                                                            borderRadius: 4,
                                                             '&::before': {
                                                                backgroundImage: `url(${bankBg})`,
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        <Box className="bank-icon-wrapper">
                                                            <span
                                                                className="icon-bank"
                                                                style={{
                                                                    fontSize: 24,
                                                                    color: colors.primaryLight[100],
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography variant="overline" className="account-type-label">
                                                            {account.accountType}
                                                        </Typography>

                                                        <Typography variant="subtitle1" mt={0.2} className="bank-name-text">
                                                            {account.bankName}
                                                        </Typography>

                                                        <Stack className="account-info-container">
                                                            <Box>
                                                                <Typography variant="overline" className="info-label">
                                                                    {t('payments.createPayment.swiftBicCode')}
                                                                </Typography>
                                                                <Typography variant="subtitle1" className="info-value">
                                                                    {account.swiftBicCode}
                                                                </Typography>
                                                            </Box>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Stack>
                                                                    <Typography variant="overline" className="info-label">
                                                                        {t('payments.createPayment.accountNumber')}
                                                                    </Typography>
                                                                    <Stack className="account-number-container">
                                                                        <Typography variant="subtitle1" className="info-value">
                                                                            {isAccountNumberVisible
                                                                                ? `**** **** ${account.accountNumber}`
                                                                                : '**** **** 5678'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </BankAccountCardWrapper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )} */}
                            {/* Add Card Button - commented out (Credit & Debit Cards functionality) */}
                            {/* {paymentMethod === 'card' && (
                                <ButtonComponent
                                    variant="outlined"
                                    startIcon={<span className="icon-plus" style={{ fontSize: '20px', lineHeight: 1 }} />}
                                    onClick={() => setShowCardForm(!showCardForm)}
                                    sx={{ width: 'fit-content' }}
                                    MDButton={true}>
                                    {t('payments.createPayment.addCard')}
                                </ButtonComponent>
                            )} */}
                            <ButtonComponent
                                variant="contained"
                                onClick={handleMakePayment}
                                MDButton={true}
                                disabled={!isPaymentMethodsEnabled()}
                            >
                                {t('payments.createPayment.buttons.makePayment') || 'Make a Payment'}
                            </ButtonComponent>

                            {/* Card Details Form - commented out (Credit & Debit Cards functionality) */}
                            {/* {paymentMethod === 'card' && showCardForm && (
                                <Stack
                                    sx={{
                                        borderRadius: 4,
                                        border: `1px solid ${colors.bluePreset[70]}`,
                                        bgcolor: colors.bluePreset[50],
                                        p: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Card Number */}
                            {/* <Box>
                                            <TextFieldComponent
                                                required
                                                value={cardNumber}
                                                onChange={e => setCardNumber(e.target.value)}
                                                label={t('payments.createPayment.enterCardNumber')}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    },
                                                }}
                                            />
                                        </Box> */}

                            {/* Date and CVV in a row */}
                            {/* <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box>
                                                    <TextFieldComponent
                                                        required
                                                        value={
                                                            expiryMonth && expiryYear
                                                                ? `${expiryMonth}/${expiryYear}`
                                                                : ''
                                                        }
                                                        onChange={e => {
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            if (value.length <= 4) {
                                                                if (value.length <= 2) {
                                                                    setExpiryMonth(value);
                                                                    setExpiryYear('');
                                                                } else {
                                                                    setExpiryMonth(value.slice(0, 2));
                                                                    setExpiryYear(value.slice(2, 4));
                                                                }
                                                            }
                                                        }}
                                                        label="MM/YY"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box>
                                                    <TextFieldComponent
                                                        required
                                                        value={cvv}
                                                        onChange={e => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                            setCvv(value);
                                                        }}
                                                        label={t('payments.createPayment.cvv')}
                                                        type="text"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid> */}

                            {/* Name on Card */}
                            {/* <Box>
                                            <TextFieldComponent
                                                required
                                                value={nameOnCard}
                                                onChange={e => setNameOnCard(e.target.value)}
                                                label={t('payments.createPayment.enterNameOnCard')}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    },
                                                }}
                                            />
                                        </Box> */}

                            {/* Action Buttons */}
                            {/* <Stack direction={'row'} gap={2}>
                                            <ButtonComponent
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowCardForm(false);
                                                    setCardNumber('');
                                                    setExpiryMonth('');
                                                    setExpiryYear('');
                                                    setCvv('');
                                                    setNameOnCard('');
                                                }}
                                            >
                                                {t('payments.createPayment.buttons.cancel')}
                                            </ButtonComponent>
                                            <ButtonComponent
                                                variant="contained"
                                                onClick={() => {
                                                    // TODO: Handle add card logic
                                                    setShowCardForm(false);
                                                }}
                                            >
                                                {t('payments.createPayment.buttons.save')}
                                            </ButtonComponent>
                                        </Stack>
                                    </Box>
                                </Stack>
                            )} */}
                        </Stack>
                    </Stack>
                </Grid>

                {/* Right Column - Payment Summary */}
                <Grid size={{ xs: 12, md: 4 }} sx={{ position: { md: 'sticky' }, top: { md: 0 }, height: '100%' }}>
                    <Stack className="paymentSummaryWrapper" p={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: colors.primaryLight[100] }}>
                                    {t('payments.createPayment.contractId')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {selectedContract
                                        ? contracts.find(c => c.id === selectedContract)?.aspireId ||
                                          contracts.find(c => c.id === selectedContract)?.label ||
                                          `Contract ${selectedContract}`
                                        : contractFromState?.aspireId || contractFromState?.id || 'N/A'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: colors.primaryLight[100] }}>
                                    {t('payments.createPayment.paymentDate')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {whenToPay === 'today'
                                        ? formattedToday
                                        : customDate
                                          ? customDate.format('MM/DD/YYYY')
                                          : 'MM/DD/YYYY'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('payments.createPayment.paymentAmount')}
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'text.primary',
                                        fontWeight: 700,
                                    }}
                                >
                                    {formatCurrency(paymentAmount || 0)}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>

            {/* Add Account Drawer */}
            {isPaymentMethodsEnabled() && paymentMethod === 'bank' && (
                <CustomDrawer
                    open={showAddAccountForm}
                    onClose={handleCloseAddAccountDrawer}
                    title={t('payments.createPayment.addAccount')}
                    width={380}
                    sticky={true}
                    footer={
                        <Stack direction="row" gap={2} justifyContent="flex-end" pt={3}>
                            <ButtonComponent
                                variant="outlined"
                                onClick={handleCloseAddAccountDrawer}
                                sx={{ width: 'fit-content' }}
                            >
                                {t('payments.createPayment.buttons.cancel')}
                            </ButtonComponent>
                            <ButtonComponent
                                variant="contained"
                                onClick={handleAddAccount}
                                sx={{ width: 'fit-content' }}
                            >
                                {t('payments.createPayment.addAccount')}
                            </ButtonComponent>
                        </Stack>
                    }
                >
                    <Stack gap={2}>
                        {/* Country */}
                        <CustomSelect
                            title={t('payments.createPayment.country')}
                            ariaLabel={t('payments.createPayment.country')}
                            labelSrOnly
                            required
                            value={country}
                            onChange={value => {
                                setCountry(value);
                                if (addAccountFormErrors.country) {
                                    setAddAccountFormErrors(prev => ({ ...prev, country: '' }));
                                }
                            }}
                            options={['United States', 'Canada']}
                            placeholder={t('payments.createPayment.selectYourCountry')}
                            errorValue={addAccountFormErrors.country}
                            sx={customSelectFieldSx}
                        />

                        {/* Account Type */}
                        <CustomSelect
                            title={t('payments.createPayment.accountType')}
                            ariaLabel={t('payments.createPayment.accountType')}
                            labelSrOnly
                            required
                            value={accountType}
                            onChange={value => {
                                setAccountType(value);
                                if (addAccountFormErrors.accountType) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        accountType: '',
                                    }));
                                }
                            }}
                            options={[
                                t('payments.createPayment.accountTypes.savingsAccount'),
                                t('payments.createPayment.accountTypes.checkingAccount'),
                            ]}
                            placeholder={t('payments.createPayment.enterAccountName')}
                            errorValue={addAccountFormErrors.accountType}
                            sx={customSelectFieldSx}
                        />

                        {/* Bank Name */}
                        <TextFieldComponent
                            label={t('payments.createPayment.bankName') || 'Bank Name'}
                            required
                            value={bankName}
                            maxLength={BANK_NAME_MAX_LENGTH}
                            onChange={e => {
                                setBankName(sanitizeBankNameInput(e.target.value));
                                if (addAccountFormErrors.bankName) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        bankName: '',
                                    }));
                                }
                            }}
                            errorValue={addAccountFormErrors.bankName}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        <TextFieldComponent
                            label={t('payments.createPayment.nickName')}
                            value={nickName}
                            maxLength={30}
                            onChange={e => setNickName(sanitizeBankNameInput(e.target.value))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        {/* Routing Number */}
                        <TextFieldComponent
                            label={t('payments.createPayment.routingNumber')}
                            required
                            value={routingNumber}
                            onChange={e => {
                                // Only allow numeric input
                                const numericValue = e.target.value.replace(/\D/g, '');
                                setRoutingNumber(numericValue);
                                if (addAccountFormErrors.routingNumber) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        routingNumber: '',
                                    }));
                                }
                            }}
                            errorValue={addAccountFormErrors.routingNumber}
                            maxLength={9}
                            preventCopy={true}
                            showPasswordToggle={true}
                            onToggleVisibility={setShowFormRoutingNumber}
                            type={showFormRoutingNumber ? 'text' : 'password'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        {/* Re-enter Routing Number */}
                        <TextFieldComponent
                            label="Re-enter Routing Number"
                            required
                            value={reEnterRoutingNumber}
                            onChange={e => {
                                // Only allow numeric input
                                const numericValue = e.target.value.replace(/\D/g, '');
                                setReEnterRoutingNumber(numericValue);
                                if (addAccountFormErrors.reEnterRoutingNumber) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        reEnterRoutingNumber: '',
                                    }));
                                }
                            }}
                            errorValue={addAccountFormErrors.reEnterRoutingNumber}
                            maxLength={9}
                            preventCopy={true}
                            showPasswordToggle={true}
                            onToggleVisibility={setShowFormReEnterRoutingNumber}
                            type={showFormReEnterRoutingNumber ? 'text' : 'password'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        {/* Account Number */}
                        <TextFieldComponent
                            label={t('payments.createPayment.accountNumber')}
                            required
                            value={accountNumber}
                            onChange={e => {
                                // Max 16 characters
                                const value = e.target.value.slice(0, 16);
                                setAccountNumber(value);
                                if (addAccountFormErrors.accountNumber) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        accountNumber: '',
                                    }));
                                }
                            }}
                            errorValue={addAccountFormErrors.accountNumber}
                            maxLength={16}
                            preventCopy={true}
                            showPasswordToggle={true}
                            onToggleVisibility={setShowFormAccountNumber}
                            type={showFormAccountNumber ? 'text' : 'password'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        {/* Re-enter Account Number */}
                        <TextFieldComponent
                            label={t('payments.createPayment.reEnterAccountNumber')}
                            required
                            value={reEnterAccountNumber}
                            onChange={e => {
                                // Max 16 characters
                                const value = e.target.value.slice(0, 16);
                                setReEnterAccountNumber(value);
                                if (addAccountFormErrors.reEnterAccountNumber) {
                                    setAddAccountFormErrors(prev => ({
                                        ...prev,
                                        reEnterAccountNumber: '',
                                    }));
                                }
                            }}
                            errorValue={addAccountFormErrors.reEnterAccountNumber}
                            maxLength={16}
                            preventCopy={true}
                            showPasswordToggle={true}
                            onToggleVisibility={setShowFormReEnterAccountNumber}
                            type={showFormReEnterAccountNumber ? 'text' : 'password'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Stack>
                </CustomDrawer>
            )}

            {/* Payment Confirmation Modal */}
            <CommonModal
                modalOpen={showConfirmationModal}
                onCloseModal={handleCloseConfirmationModal}
                DialogMainTitle="Confirmation"
                primaryBtnName="Close"
                secondaryBtnName="Confirm"
                primaryBtnClick={handleCloseConfirmationModal}
                secondaryBtnClick={handleConfirmPayment}
                ModalMaxWidth={600}
            >
                <Box sx={{ py: 1 }}>
                    {(() => {
                        // Get user's full name
                        const getUserFullName = () => {
                            return (userDetails?.Name as string) || 'User';
                        };

                        // Get account number ending (last 4 digits)
                        const getAccountEnding = () => {
                            if (selectedAccountId) {
                                const selectedAccount = savedBankAccounts.find(acc => acc.id === selectedAccountId);
                                if (selectedAccount?.accountNumber) {
                                    return selectedAccount.accountNumber.slice(-4);
                                }
                            }
                            return '----';
                        };

                        // Format payment date
                        const getPaymentDate = () => {
                            if (whenToPay === 'today') {
                                return formatInET(new Date());
                            } else if (whenToPay === 'custom' && customDate) {
                                return customDate.format('MM/DD/YYYY');
                            }
                            return 'MM/DD/YYYY';
                        };

                        const userName = getUserFullName();
                        const accountEnding = getAccountEnding();
                        const paymentDate = getPaymentDate();
                        const amount = paymentAmount || '0.00';

                        return (
                            <Stack spacing={2}>
                                <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                                    Please read: By clicking the button to submit this payment, you,{' '}
                                    <strong>{userName}</strong>, authorize a debit of{' '}
                                    <strong>{formatCurrency(amount)}</strong> from your account(s) ending in{' '}
                                    <strong>{accountEnding}</strong>. You have requested that the payment be scheduled
                                    for <strong>{paymentDate}</strong>. The withdrawal from your account will generally
                                    occur within two business days of this date, subject to your bank&apos;s processing
                                    schedule.
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                                    Application of payment is subject to receipt of good funds. Credit for this payment
                                    may not be applied until actual receipt of funds.{' '}
                                    <strong>
                                        If this payment is time-sensitive, contact us to review payment options.
                                    </strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                                    Scheduled transactions may be cancelled prior to the payment date. Payments will be
                                    applied in accordance with the terms of the underlying agreement(s).
                                </Typography>
                            </Stack>
                        );
                    })()}
                </Box>
            </CommonModal>
        </MakePaymentWrapper>
    );
};
